import { useI18n } from '@ecom/i18n';
import type { CSSProperties, ReactNode } from 'react';

import { cx } from '../utils/cx';

export type SkeletonShape = 'text' | 'block' | 'circle';

export interface SkeletonProps {
  readonly shape?: SkeletonShape;
  /** Any CSS length. Defaults to filling the container. */
  readonly width?: string;
  readonly height?: string;
  /** Number of stacked lines. Only meaningful for `shape="text"`. */
  readonly lines?: number;
  /**
   * Announce the placeholder as loading. Set this on exactly ONE skeleton per
   * loading region — see the note below on why it is off by default.
   */
  readonly labelled?: boolean;
}

/**
 * Loading placeholder (§7.1 primitives).
 *
 * No skeleton exists in the prototypes (they render fully-populated demo
 * data), so this is built from existing tokens only: `--ecom-surface-2` as
 * the base with a sweep toward `--ecom-surface-hover`. No new colour was
 * added.
 *
 * Silent by default, and that is the accessibility decision here rather than
 * an oversight. A skeleton is decorative — it stands in for content that does
 * not exist yet — and a product grid rendering twelve of them would otherwise
 * announce "loading" twelve times. So each skeleton is `aria-hidden`, and the
 * caller marks a single one with `labelled` to speak for the whole region.
 *
 * The shimmer is a CSS animation, so `prefers-reduced-motion: reduce` in
 * tokens.css already neutralises it globally — motion sensitivity is handled
 * once for the design system, not re-implemented per component.
 */
export function Skeleton({
  shape = 'text',
  width,
  height,
  lines = 1,
  labelled = false,
}: SkeletonProps): ReactNode {
  const { t } = useI18n();

  const style: CSSProperties = {
    ...(width !== undefined ? { inlineSize: width } : {}),
    ...(height !== undefined ? { blockSize: height } : {}),
  };

  const accessibility = labelled
    ? ({ role: 'status', 'aria-label': t('common.loading') } as const)
    : ({ 'aria-hidden': true } as const);

  if (shape === 'text' && lines > 1) {
    return (
      <div className="ecom-skeleton__group" {...accessibility}>
        {Array.from({ length: lines }, (_, index) => (
          <span
            key={index}
            className={cx('ecom-skeleton', 'ecom-skeleton--text')}
            // The last line of a paragraph is rarely full width; stopping
            // short of the edge is what makes a text skeleton read as text.
            style={index === lines - 1 ? { ...style, inlineSize: '60%' } : style}
          />
        ))}
      </div>
    );
  }

  return (
    <span
      className={cx('ecom-skeleton', `ecom-skeleton--${shape}`)}
      style={style}
      {...accessibility}
    />
  );
}
